import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Grid, Typography, useMediaQuery, MenuItem } from '@mui/material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import QuickLinks from './Components/QuickLinks';
import { BenefitsSection, TestimonialsSection, ClubCardsSection, TestimonialVideoModal } from './Components';
import { useDispatch, useSelector } from 'react-redux';
import { decodeToken } from '../../utils/settings';
import { getHomepageData } from './Action';
import { toast } from 'react-toastify';
import customToast, { showApiResponseToast } from '../../utils/customToast';
import { addressDetailAction } from '../../v2/Address/APIs/action';
import IconReusable from '../../components/IconReusable';
import { useNavigate } from 'react-router-dom';
import { processVideoData } from '../../utils/videoUtils';
import { getCompanySuppliersAction } from '../../v2/APIs/action';
import { RoleAddtionAction } from '../FirmOnboarding/API/Action';
import ProfilerCards from '../Profiler/Cards/ProfilerCards';
import { getModulerProfilerAction } from '../Profiler/API/Action';
import { fetchSuppliers } from '../Common/Entity/actions/reduxActions';
import * as Sentry from '@sentry/react';

const HomePage = () => {
  const { loginUser, is_PWA } = useSelector((state) => {
    return {
      loginUser: state.loginReducer.loginUser,
      is_PWA: state.loginReducer.is_PWA,
    };
  });

  const dashboardConfig = useSelector(
    (state) => state.dashboardReducer.dashboardConfig,
  );
  const pendingRoleAdditionRequests = useSelector(
    (state) => state.FirmOnboardingReducer?.pending_role_addition_requests || [],
  );
  const pendingRoleAdditionRequest = pendingRoleAdditionRequests[0];

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [addressDetails, setAddressDetails] = useState(null);
  const [tokenData, setTokenData] = useState({});
  const [companyEsiId, setCompanyEsiId] = useState(null);
  const getTokenData = async () => {
    try {
      const decoded = await decodeToken();
      setTokenData(decoded);
      setFirmOnboarded(decoded?.default_company_info);
    } catch (error) {
      Sentry.captureException(error, { level: 'warning', tags: { area: 'homepage', stage: 'decode_token' } });
    }
  };
  useEffect(() => {
    getTokenData();
  }, []);
  const allowedKeys = tokenData?.quicklink_config ?? [];
  const [homeData, setHomeData] = useState({});
  const [firmOnboarded, setFirmOnboarded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [videos, setVideos] = useState([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);
  const isRoleAdditionInProgressRef = useRef(false);
  const isMobile = useMediaQuery('(max-width: 1200px)');
  const [profilerData, setProfilerData] = useState(null);

  const { selectedSupplier: selectedManufacturer, selectedEntity: selectedCompanyEntity } = useSelector(
    (state) => state.entityReducer
  );
  const manufacturer_id = selectedManufacturer ? selectedManufacturer : null;
  const getHomeData = async () => {
    setLoading(true);
    Sentry.addBreadcrumb({
      category: 'homepage',
      level: 'info',
      message: 'Calling getHomepageData',
      data: { manufacturer_id },
    });
    const { error, message, data } = await getHomepageData(manufacturer_id);
    setLoading(false);
    if (!error) {
      setHomeData(data);
    } else {
      toast.error(message);
      Sentry.captureMessage('getHomepageData failed', {
        level: 'warning',
        tags: { area: 'homepage', stage: 'get_home_data' },
        extra: { manufacturer_id, message },
      });
    }
  };
  const { banner_section, benefits_section, testimonial_section, welcome_section, club_cards } = homeData;

  useEffect(() => {
    if (testimonial_section?.videos) {
      const processedVideos = processVideoData(testimonial_section?.videos);
      setVideos(processedVideos);
    }
  }, [testimonial_section]);

  const testimonialSliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    arrows: false,
    pauseOnHover: true,
    autoplay: true,
    slidesToShow: 5,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1550,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1300,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
        },
      },
      
    ],
  };
  const bannerSliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 3000,
    slidesToShow: 5,
    slidesToScroll: 1,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1535,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 1300,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      
    ],
  };
  const handleVideoClick = (index) => {
    setSelectedVideoIndex(index);
    setVideoModalOpen(true);
    setVideoError(false);
    setCurrentTime(0);
    setDuration(0);
  };
  const handleCloseVideoModal = () => {
    setVideoModalOpen(false);
    setCurrentTime(0);
    setDuration(0);
    setVideoError(false);
  };
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      setCurrentTime(currentTime);
      if (duration && !isNaN(duration)) {
        setDuration(duration);
      }
    }
  };
  const handleSeek = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };
  const handleVideoElementClick = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play()
          .then(() => console.log('Manual play succeeded'))
          .catch(e => console.log('Manual play failed:', e));
      }
    }
  };
  const handleVideoEnded = () => {
    if (selectedVideoIndex < videos.length - 1) {
      setSelectedVideoIndex(selectedVideoIndex + 1);
      setCurrentTime(0);
    } else {
      setSelectedVideoIndex(0);
      setCurrentTime(0);
    }
  };
  useEffect(() => {
    if (videoModalOpen && videoRef.current) {
      const timer = setTimeout(() => {
        videoRef.current.play()
          .then(() => console.log('Auto-play next video succeeded'))
          .catch(e => console.log('Auto-play next video failed:', e));
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [selectedVideoIndex, videoModalOpen]);

  useEffect(() => {
    getHomeData();
  }, [loginUser,selectedManufacturer]);

  useEffect(() => {
    if (!pendingRoleAdditionRequest || isRoleAdditionInProgressRef.current) {
      return;
    }

    const runPendingRoleAddition = async () => {
      isRoleAdditionInProgressRef.current = true;
      try {
        const { error, message, data } = await RoleAddtionAction(pendingRoleAdditionRequest);
        if (!error) {
          if (data?.response_meta) {
            showApiResponseToast(data.response_meta, message);
          } else if (message) {
            customToast.success(message);
          }
        } else {
          customToast.error(message || 'Unable to create role');
        }
      } finally {
        dispatch({ type: 'DEQUEUE_PENDING_ROLE_ADDITION_REQUEST' });
        isRoleAdditionInProgressRef.current = false;
      }
    };

    runPendingRoleAddition();
  }, [dispatch, pendingRoleAdditionRequest]);

  const profilerRequestIdRef = useRef(0);
  const fetchProfilerData = async (module) => {
    const requestId = ++profilerRequestIdRef.current;
    setLoading(true);
    const { error, data, messsage } = await getModulerProfilerAction(module);
    if (requestId !== profilerRequestIdRef.current) return;
    setLoading(false);
    if(!error){
      setProfilerData(data);
    }else{
      setProfilerData(null);
    }
  };
  useEffect(() => {
    if(loginUser){
      fetchProfilerData('home');
    }
  }, [loginUser]);

  const handleProfilerQuestionsChange = useCallback((questions) => {
    setProfilerData((prev) => {
      const next = Array.isArray(questions) ? questions : [];
      if (!prev || (Array.isArray(prev.questions) && prev.questions === next)) {
        return prev;
      }
      if (
        Array.isArray(prev.questions) &&
        prev.questions.length === next.length &&
        prev.questions.every((q, i) => q === next[i])
      ) {
        return prev;
      }
      return { ...prev, questions: next };
    });
  }, []);

  useEffect(() => {
    dispatch(fetchSuppliers());
  }, [dispatch]);

  return (
    <>
      {loginUser &&  !firmOnboarded && (
        <Box sx={{ display: 'flex', 
          p: { xs: 2, md: 0 },
          mr: { xs: 0, md: 4 }, 
          ml: {xs: 0, md: 2},
          pb: { xs: 2, md: 2 },
          position: {xs: 'sticky', md: 'relative'}, 
          top: 0, 
          zIndex: 1000, 
          boxShadow: {xs: '0px 2px 8px 0px #0000001A', md: 'none'},
          background: {xs: '#FAFAFA', md: 'transparent'},
        }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant='h2'>
              Add your Firm
            </Typography>
            <Box>
              <Box 
                onClick={() => {
                  navigate('/firm-onboarding/select-manufacturer');
                  dispatch({ type: 'RESET_FIRM_ONBOARDING' });
                }}
                sx={{
                  background: '#fff',
                  height: 60,
                  width: 60,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 2,
                  p: '10px',
                  cursor: 'pointer',
                }}
              >
                <IconReusable name="Add" size={24} color="#1D1B20" />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {welcome_section && (
            <Box sx={{ display: 'flex', flexDirection: 'column', px: { xs: 2, lg: 2 }, }}>
              <Typography 
                variant="h1"
                sx={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  hyphens: 'auto',
                }}
              >
                {welcome_section?.header_desc?.[0]}
              </Typography>
              <Typography 
                variant="h1"
                sx={{
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  hyphens: 'auto',
                }}
              >
                {welcome_section?.header_desc?.[1]}
              </Typography>
            </Box>
          )}
          {banner_section?.sliders && (
            <Box sx={{
              pl: { xs: 2, lg: 2 },
              overflow: 'hidden',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1,
                maxWidth: { 
                  xs:'620px',   // 2 slides (below 600px)
                  sm: '930px',  // 3 slides (600px - 899px)
                  md: '1240px',  // 3 slides (900px - 1299px)
                  lg: '1240px',   // 4 slides (1300px+)
                  xl: '1550px',   // 5 slides (1300px+)
                },
                width: 'fit-content',
                overflowX: 'scroll', 
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}>
                <Box sx={{
                  width: '100%',
                  scrollbarWidth: 'none',
                }}>
                  <Slider {...bannerSliderSettings}>
                    {banner_section.sliders.map((slider, idx) => (
                      <Box key={idx} 
                        sx={{
                          borderRadius: 2,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          position: 'relative',
                          width: '300px',
                        }}
                      >
                        <Box
                          component='img'
                          src={slider}
                          alt={`Slider ${idx + 1}`}
                          sx={{
                            width: '300px',
                            objectFit: 'contain',
                            display: 'block',
                          }}
                        />
                      </Box>
                    ))}
                  </Slider>
                </Box>
              </Box>  
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2}>
            {loginUser && firmOnboarded && club_cards && (
              <Grid item xs={12} md={6} xl={4}> 
              <Box sx={{ pl: { xs: 2, lg: 2 }, pr: { xs: 2, lg: 0 }}}>
                <ClubCardsSection 
                  clubCards={club_cards} 
                  onActionClick={(club) => {
                    navigate('/create-po');
                  }}
                />
                </Box>
              </Grid> 
            )}
            {benefits_section && (
              <Grid item xs={12} md={6}> 
                <Box sx={{ pl: { xs: 2, lg: 2 }}}>
                  <BenefitsSection 
                    benefits_section={benefits_section}
                  />
                </Box>
              </Grid>
            )}
            {testimonial_section && !firmOnboarded && (
                <Grid item xs={12} md={6}> 
                  <Box sx={{ pl: { xs: 2, lg: 0 }, pr: { xs: 2, lg: 2},}}>
                    <TestimonialsSection 
                      testimonial_section={testimonial_section} 
                      testimonialSliderSettings={testimonialSliderSettings}
                      handleVideoClick={handleVideoClick}
                    />
                  </Box>
                </Grid> 
            )}
            {isMobile && loginUser && firmOnboarded && selectedManufacturer && dashboardConfig?.quick_link_config?.length !== 0 && (
              <Grid item xs={12} md={6}> 
                <Box sx={{ px: { xs: 2, lg: 2 }}}>
                  <QuickLinks
                    allowedKeys={dashboardConfig}
                    firmOnboarded={firmOnboarded}
                    manufacturerSelected={selectedManufacturer}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
          
          <TestimonialVideoModal
            open={videoModalOpen}
            onClose={handleCloseVideoModal}
            videos={videos}
            selectedVideoIndex={selectedVideoIndex}
            videoRef={videoRef}
            currentTime={currentTime}
            duration={duration}
            onSeek={handleSeek}
            onTimeUpdate={handleTimeUpdate}
            onVideoClick={handleVideoElementClick}
            onEnded={handleVideoEnded}
            videoError={videoError}
          />
          {profilerData?.questions.length > 0 && (
            <Box sx={{ px: {xs: 0, md: 2} }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #E9F0F6, #F3EBDD)',
                border: '1px solid rgba(255,255,255,0.6)',
                borderRight: 'none !important',
                boxShadow: '0px 6px 16px rgba(0,0,0,0.05)',
                borderRadius: 2,
                py: 2,
                pl: { xs: 2, lg: 2 },
                pr: { xs: 0, lg: 0 },
                gap: 2,
                display: 'flex',
                flexDirection: 'column',
              }}>
                <Typography variant='h2' sx={{
                  fontSize: '24px !important',
                  lineHeight: '32px !important',
                  fontFamily: "Kalam",
                  fontWeight: 700,
                  fontStyle: 'normal',
                  color:'#995A00',
                }}>
                  Help us serve you better
                </Typography>
                <ProfilerCards
                  initialProfiler={profilerData}
                  module='home'
                  onQuestionsChange={handleProfilerQuestionsChange}
                />
              </Box>
            </Box>
          )}

          <Box sx={{ width: '100%', px: { xs: 2, lg: 2 } }}>
            <Typography
              variant='title'
              sx={{ color: '#666'}}
            >
              <Typography
                component='span'
                variant='title'
                sx={{ color: '#FFA525' }}
              >
                Goodness
              </Typography>{' '}
              is our only Business
            </Typography>
            <Typography
              variant='labelD'
              sx={{ color: '#8C8C8C' }}
            >
              World’s #1 business community welfare platform
            </Typography>
          </Box>
        </Box>
      </Box>

    </>
  );
};

export default HomePage;